/**
 * Erkennt, ob seit dem Laden dieser Sitzung eine neue Fassung ausgeliefert
 * wurde.
 *
 * Warum ueberhaupt: Die Anwendung ist eine einzige Seite. Wer sie morgens
 * oeffnet und den Reiter offen laesst, sieht am Abend noch den Stand vom
 * Morgen — auch wenn zwischendurch dreimal ausgeliefert wurde. Genau das ist
 * hier passiert: neue Kapitel waren live und im Browser trotzdem nicht zu
 * sehen.
 *
 * Woran wir es erkennen: an der Adresse des Einstiegsskripts. Vite haengt
 * jedem Buendel einen Inhalts-Hash an — aendert sich der Inhalt, aendert sich
 * die Adresse. Wir holen also `index.html` (Cache ausdruecklich uebergangen,
 * und Netlify liefert sie ohnehin mit `max-age=0`) und vergleichen das darin
 * genannte Einstiegsskript mit dem, das dieses Fenster geladen hat.
 *
 * Im Entwicklungsserver steht dort `/src/main.tsx` — unveraenderlich. Der
 * Vergleich schlaegt dort also nie an, was richtig ist: dafuer gibt es HMR.
 */

const INTERVALL = 5 * 60 * 1000
const MINDESTABSTAND = 60 * 1000

/** Adresse des Einstiegsskripts, das dieses Fenster geladen hat. */
function eigenesSkript(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src]')
  return el ? new URL(el.src, document.baseURI).pathname : null
}

/** Dasselbe, aber aus der frisch geholten `index.html`. */
async function ausgeliefertesSkript(signal: AbortSignal): Promise<string | null> {
  const url = new URL(import.meta.env.BASE_URL + 'index.html', location.href)
  url.searchParams.set('v', String(Date.now()))
  const antwort = await fetch(url, { cache: 'no-store', signal })
  if (!antwort.ok) return null
  const html = await antwort.text()
  const treffer = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/i)
  if (!treffer?.[1]) return null
  return new URL(treffer[1], document.baseURI).pathname
}

/**
 * Ruft `beiNeuerFassung` genau einmal auf, sobald eine neue Fassung erkannt
 * wurde. Gibt die Abmeldefunktion zurueck.
 */
export function beobachteFassung(beiNeuerFassung: () => void): () => void {
  const eigen = eigenesSkript()
  if (!eigen) return () => {}

  const abbruch = new AbortController()
  let letzte = Date.now()
  let fertig = false

  async function pruefen() {
    if (fertig || document.visibilityState === 'hidden') return
    letzte = Date.now()
    try {
      const live = await ausgeliefertesSkript(abbruch.signal)
      if (live && live !== eigen) {
        fertig = true
        beiNeuerFassung()
      }
    } catch {
      // Netz weg, Deploy gerade im Gang, Seite wird verlassen: alles Faelle,
      // in denen Schweigen die richtige Antwort ist. Der naechste Durchgang
      // kommt von allein.
    }
  }

  const takt = window.setInterval(pruefen, INTERVALL)

  // Ein Reiter, der Stunden im Hintergrund lag, ist der haeufigste Fall.
  // Beim Zurueckkommen sofort nachsehen — aber nicht bei jedem Alt-Tab.
  const beiSichtbar = () => {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - letzte < MINDESTABSTAND) return
    void pruefen()
  }
  document.addEventListener('visibilitychange', beiSichtbar)

  return () => {
    fertig = true
    abbruch.abort()
    window.clearInterval(takt)
    document.removeEventListener('visibilitychange', beiSichtbar)
  }
}
